import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { parseApiError } from '../../api/errors';
import { Button, Input, AlertBanner } from '../../components/common';

export const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await apiClient.POST('/api/v1/auth/forgot-password', {
        body: { email: email.trim() },
      });

      if (error) {
        throw new Error(parseApiError(error).message);
      }

      setIsSubmitted(true);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to submit password reset request.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-4 space-y-4">
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Check your email
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
          If an account exists for <span className="font-semibold text-slate-800 dark:text-slate-200">{email}</span>, we have sent instructions to reset your password.
        </p>
        <div className="pt-2">
          <Link to="/login">
            <Button variant="outline" size="sm" className="w-full">
              Return to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      {errorMessage && (
        <AlertBanner
          variant="error"
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <p className="text-xs text-slate-600 dark:text-slate-400">
        Enter your registered email address and we'll send you a link to reset your password.
      </p>

      <div>
        <Input
          type="email"
          label="Email Address"
          placeholder="shopper@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={isLoading}
        />
      </div>

      <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading}>
        Send Reset Link
      </Button>

      <div className="text-center pt-2">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Sign In</span>
        </Link>
      </div>
    </form>
  );
};
